'use client';

import { useEffect, useRef } from 'react';
import { useStore } from '@/store/store';
import { getConfig } from '@/utils/config/config';
import { MdsClient } from '@/lib/mds/mds-client';
import { ddRumError } from '@/utils/datadog';
import { ddErrorMdsInit } from '@/utils/datadog/site-wide-jackpot';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies/constants';
import { createSwjMessageHandler } from './utils/swj-message-handler';
import { checkSiteWideJackpotsUserOptInStatus } from './utils/swj-opt-in-manager';
import { SWJ_MESSAGE_TYPE } from './utils/swj-types';
import type { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';

// Multiplier values for site-wide jackpot opt-in status
// 0 = opted out / no multiplier
// 1 = 1x multiplier (standard opt-in)
// 2 = 2x multiplier (boosted opt-in)
const MULTIPLIER_OPTED_OUT = 0;

/**
 * Hook for initializing and managing the MDS consumer for real-time opt-in status updates.
 */
export const useMdsConsumer = (jackpotData: SiteWideJackpotData) => {
    const jackpotId = jackpotData?.provider_jackpot_id?.[0]?.text || '';
    const enabled = jackpotData.enabled ?? false;
    const config = getConfig();
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const setMultiplier = useStore((state) => state.siteWideJackpot.setMultiplier);
    const setHasSwjInitialized = useStore((state) => state.siteWideJackpot.setHasSwjInitialized);
    const setOptInStatusError = useStore((state) => state.siteWideJackpot.setOptInStatusError);

    const isInitializedRef = useRef(false);
    const messageHandlerRef = useRef<((message: any) => void) | null>(null);
    const mdsEndpointRef = useRef<string | null>(null);

    useEffect(() => {
        const userId = getCookieValue(COOKIES.USER_ID) as string;
        const authToken = getCookieValue(COOKIES.AUTH_TOKEN) as string;

        // Require both isLoggedIn state AND valid cookies to prevent 401 errors
        if (!enabled || !isLoggedIn || !userId || !authToken || !jackpotId || isInitializedRef.current) {
            return () => {};
        }

        const initializeMds = async () => {
            const region = getCookieValue(COOKIES.REGION) as string;
            const mdsBaseUrl = config?.apis?.MdsClient?.baseUrl;

            if (!mdsBaseUrl) {
                return;
            }

            const mdsEndpoint = mdsBaseUrl.replace('statePlaceholder', region?.toLowerCase() || 'mi');
            mdsEndpointRef.current = mdsEndpoint;

            try {
                const mdsClient = MdsClient.getInstance(mdsEndpoint);
                messageHandlerRef.current = createSwjMessageHandler(jackpotId, setMultiplier);
                mdsClient.on(SWJ_MESSAGE_TYPE, messageHandlerRef.current);
                mdsClient.initSocket(userId);
                isInitializedRef.current = true;
            } catch (error) {
                ddRumError(ddErrorMdsInit(error as Error, { jackpotId }));
            }

            const gthBaseUrl = config?.apis?.SiteWideJackpotClient?.host?.replace(
                'statePlaceholder',
                region?.toLowerCase() || 'mi'
            );

            if (gthBaseUrl) {
                await checkSiteWideJackpotsUserOptInStatus(`https://${gthBaseUrl}`, jackpotId, {
                    onOptInStatusChange: (value) => setMultiplier(value as 0 | 1 | 2),
                    onHasInitialized: () => setHasSwjInitialized(true),
                    // Reset to opted out state on error
                    onError: () => setOptInStatusError(MULTIPLIER_OPTED_OUT)
                });
            }
        };

        initializeMds();

        return () => {
            if (!isInitializedRef.current) return;

            // Cleanup MDS connection
            if (messageHandlerRef.current && mdsEndpointRef.current) {
                try {
                    const mdsClient = MdsClient.getInstance(mdsEndpointRef.current);
                    mdsClient.off(SWJ_MESSAGE_TYPE, messageHandlerRef.current);
                } catch {
                    // Instance might not exist
                }
                messageHandlerRef.current = null;
            }
            MdsClient.resetInstance();
            mdsEndpointRef.current = null;
            isInitializedRef.current = false;

            // Read current login state from store (avoids stale closure)
            const currentlyLoggedIn = useStore.getState().userContext.isLoggedIn;
            if (!currentlyLoggedIn) {
                setMultiplier(MULTIPLIER_OPTED_OUT);
                setHasSwjInitialized(undefined);
            }
        };
    }, [enabled, isLoggedIn, jackpotId, config, setMultiplier, setHasSwjInitialized, setOptInStatusError]);
};
